import URLS from "~/constants/urls"
import { ITokenInfo } from "~/types/global"
import { axiosInstance, retryFetch } from "~/utils/axios"
import { withLinkedTenant } from "~/utils/linked-tenant"

export async function getAccounts(
  tokenInfo: ITokenInfo,
  payload: {
    searchText?: string
    industries?: string[]
    locations?: string[]
    revenue?: string[]
    quicksearch?: string
  },
  linkedTenantId?: number
) {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  // Omitting `linkedTenantId` means "whichever BU the user is in", not "the base
  // tenant" — the account-plan screen called this with no id and listed base-tenant
  // accounts while the user was in a linked BU. (ENG-3729)
  const url = withLinkedTenant(
    `${prefix}tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/user_accounts`,
    linkedTenantId
  )
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: payload
  })
}

export async function getLast5EmailTemplates(tokenInfo: ITokenInfo) {
  return await axiosInstance({
    method: "GET",
    url: `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/recent-email-templates?lastN=5`,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getSavedTemplate(tokenInfo: ITokenInfo) {
  return await axiosInstance({
    method: "GET",
    url: `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/email-templates`,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getOrbitshiftLocations(tokenInfo: ITokenInfo) {
  const url = `/data/locations?version=2`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getOrbitshiftLanguages(tokenInfo: ITokenInfo) {
  const url = `/data/languages`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getTenantUsersBasicDetails(tokenInfo: ITokenInfo) {
  return await axiosInstance({
    method: "GET",
    url: `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/users_basic_details?active=true`,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function syncAccountWithSalesforce(
  tokenInfo: ITokenInfo,
  data: {
    tenant_id: number
    user_id: number
    account_id: string
    type: string
    current_user_id: number
    opportunity_ids?: number[]
    additional_fields?: any
    people_id?: number
  }
) {
  const { tenant_id, user_id, account_id, ...queryParams } = data

  const queryString = new URLSearchParams()

  if (queryParams.type) queryString.append("type", queryParams.type)
  if (queryParams.current_user_id)
    queryString.append(
      "current_user_id",
      queryParams.current_user_id.toString()
    )
  if (queryParams.opportunity_ids?.length)
    queryString.append("opportunity_ids", queryParams.opportunity_ids.join(","))
  if (queryParams.people_id !== undefined)
    queryString.append("people_id", queryParams.people_id.toString())
  if (queryParams.additional_fields)
    queryString.append(
      "additional_fields",
      JSON.stringify(queryParams.additional_fields)
    )

  const url = `/tenants/${tenant_id}/user/${user_id}/account/${account_id}/crm-integration/salesforce?${queryString.toString()}`

  return axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function syncLeadWithSalesforce(
  tokenInfo: ITokenInfo,
  data: {
    tenant_id: number
    user_id: number
    account_id: string
    people_id: number
  }
) {
  return await axiosInstance({
    method: "POST",
    url: `/crm-integration/salesforce-leadsync`,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data
  })
}

export async function getCollaborators(
  tokenInfo: ITokenInfo,
  searchText: string = ""
) {
  return await axiosInstance({
    method: "GET",
    url: `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/collaborators/suggest?searchText=${searchText}`,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function askQuestion(
  tokenInfo: ITokenInfo,
  data: {
    title: string
    description: string
  }
) {
  return await axiosInstance({
    method: "POST",
    url: `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/feedback/ask_question`,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data
  })
}

export async function downloadAccountPriorities(
  tokenInfo: ITokenInfo,
  accountId: string,
  opportunityId?: string,
  linkedTenantId?: number
) {
  let url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/accounts/${accountId}`
  if (opportunityId) {
    url += `/opportunities/${opportunityId}`
  }
  url += "/download"

  if (linkedTenantId) {
    url += `?linked_tenant_id=${linkedTenantId}`
  }
  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    responseType: "blob"
  })
}

export async function getAccountFunctionSegments(
  tokenInfo: ITokenInfo,
  accountId: string
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/account/${accountId}/job_function_segments`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function requestToUpgradeToEnterprise(tokenInfo: ITokenInfo) {
  return await axiosInstance({
    method: "POST",
    url: `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/upgrade_to_enterprise`,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function requestNewAccount(
  tokenInfo: ITokenInfo,
  payload: {
    account_name: string
    domain: string
    region: string
    note?: string
    source?: string
  }
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/accounts/request_new_account`
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: payload
  })
}

export async function getAccountOfferings(
  tokenInfo: ITokenInfo,
  accountId: string
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/accounts/${accountId}/accountOfferings`

  return await axiosInstance({
    method: "GET",
    url: url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function addOsLiteAccounts(
  tokenInfo: ITokenInfo,
  selectedAccounts: any[]
) {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  const url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/accounts/addSaveAccounts`
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: JSON.stringify({ selectedAccounts })
  })
}

export async function pinAccount(tokenInfo: ITokenInfo, accountId: number) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/accounts/${accountId}/pin`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function unpinAccount(tokenInfo: ITokenInfo, accountId: number) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/accounts/${accountId}/unpin`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function searchnewOsLiteAccounts(
  tokenInfo: ITokenInfo | null,
  inputValue: string
) {
  return await retryFetch(
    `${URLS.OS_LITE}/tenants/${tokenInfo?.tenant_id}/users/${
      tokenInfo?.user_id
    }/fetchApolloAccounts?search=${encodeURIComponent(inputValue)}`,
    {
      method: "GET",
      headers: {
        "content-type": "application/json",
        Authorization: `Bearer ${tokenInfo?.token}`
      }
    }
  )
}

export async function searchAllAccounts(
  tokenInfo: ITokenInfo,
  name: string,
  linkedTenantId?: number
) {
  const url = withLinkedTenant(
    `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/accounts/search`,
    linkedTenantId
  )
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: { name }
  })
}

export async function getAccountResearchReport(
  tokenInfo: ITokenInfo,
  accountId: string
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/accounts/${accountId}/research_report`

  return await axiosInstance({
    method: "GET",
    url: url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}
